import React from 'react';
import { Link } from 'react-router-dom';
import { Star, MapPin, Heart } from 'lucide-react';
import { getImageUrl, toggleWishlist } from '../services/api';
import { getCategoryPlaceholder } from '../utils/placeholders';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';

const ItemCard = ({ item, wishlisted = false, onWishlistChange }) => {
  const { isAuthenticated } = useAuth();

  const imageUrl = item.images?.[0]
    ? getImageUrl(item.images[0])
    : getCategoryPlaceholder(item.category);

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated) {
      toast.error('Please log in to save items');
      return;
    }
    try {
      const res = await toggleWishlist(item.id);
      if (onWishlistChange) onWishlistChange(item.id, res.action);
      toast.success(res.action === 'added' ? 'Added to wishlist' : 'Removed from wishlist');
    } catch {
      toast.error('Failed to update wishlist');
    }
  };

  return (
    <Link to={`/items/${item.id}`} className="block" data-testid={`item-card-${item.id}`}>
      <div className="bg-white border border-[#EBE8DF] rounded-2xl overflow-hidden transition-all duration-300 ease-out hover:-translate-y-1 hover:shadow-[0_12px_40px_rgba(44,51,41,0.08)]">
        {/* Image */}
        <div className="aspect-[4/3] overflow-hidden bg-[#FDFBF7] relative">
          <img
            src={imageUrl}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
            onError={(e) => {
              e.target.src = getCategoryPlaceholder(item.category);
            }}
          />
          {/* Wishlist Heart */}
          <button
            onClick={handleWishlist}
            className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/80 backdrop-blur-sm flex items-center justify-center hover:bg-white transition-colors shadow-sm"
            data-testid={`wishlist-toggle-${item.id}`}
          >
            <Heart
              className={`w-4 h-4 transition-colors ${wishlisted ? 'text-[#D97757] fill-[#D97757]' : 'text-[#596652]'}`}
            />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Category Badge */}
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#8C9A76]/15 text-[#596652] mb-2">
            {item.category}
          </span>

          {/* Title */}
          <h3 className="font-heading font-medium text-[#2C3329] text-lg mb-1 line-clamp-1">
            {item.title}
          </h3>

          {/* Location */}
          <div className="flex items-center text-[#596652] text-sm mb-3">
            <MapPin className="w-3.5 h-3.5 mr-1" />
            <span>{item.owner_neighborhood}</span>
          </div>

          {/* Price & Rating */}
          <div className="flex items-center justify-between">
            <div>
              <span className="text-[#D97757] font-semibold text-lg">${item.price_daily}</span>
              <span className="text-[#596652] text-sm">/day</span>
            </div>
            <div className="flex items-center gap-2">
              {item.distance_km != null && (
                <span className="text-[#596652] text-xs">
                  {(item.distance_km * 0.621371).toFixed(1)} mi
                </span>
              )}
              {item.owner_rating > 0 && (
                <div className="flex items-center space-x-1">
                  <Star className="w-4 h-4 text-[#F59E0B] fill-current" />
                  <span className="text-[#2C3329] font-medium">{item.owner_rating.toFixed(1)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default ItemCard;
